import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import * as Tooltip from "@radix-ui/react-tooltip";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IManagedDbForeignKey, IManagedDbRow } from "../../services/table-editor-service";
import BooleanSvg from "../../svgs/parameter-type-icons/boolean.svg";
import DateTimeOffsetSvg from "../../svgs/parameter-type-icons/date-time-offset.svg";
import DateTimeSvg from "../../svgs/parameter-type-icons/date-time.svg";
import DateSvg from "../../svgs/parameter-type-icons/date.svg";
import DecimalSvg from "../../svgs/parameter-type-icons/decimal.svg";
import DoubleSvg from "../../svgs/parameter-type-icons/double.svg";
import GuidSvg from "../../svgs/parameter-type-icons/guid.svg";
import ImageSvg from "../../svgs/parameter-type-icons/image.svg";
import Int16Svg from "../../svgs/parameter-type-icons/int16.svg";
import Int32Svg from "../../svgs/parameter-type-icons/int32.svg";
import Int64Svg from "../../svgs/parameter-type-icons/int64.svg";
import StringSvg from "../../svgs/parameter-type-icons/string.svg";
import { KModal } from "../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../ui-kit/k-modal/k-modal-footer";
import KuikaTooltip from "../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallText from "../ui-kit/typography/SmallText/SmallText";
import {
  approxBase64Bytes,
  detectBinaryKind,
  detectImageMime,
  downloadBase64,
  extractDataUriMime,
  formatByteSize,
  IDetectedBinary,
  isDataUri,
  IDownloadOutcome,
  sanitiseFileNamePart,
  shortRowKeyForFilename,
  stripDataUriPrefix
} from "./binary-cell-helpers";
import CellEditor from "./cell-editor";
import CellExpanderModal from "./cell-expander-modal";
import { insertCellVisualState, ITableEditorCellVisualState, persistedCellVisualState } from "./cell-visual-state";
import { buildColumnFilters, IColumnFilterEntry, IColumnFilterMap } from "./column-filters";
import { orderColumnsLikeDataGrip } from "./column-order";
import { formatDatetimeOffsetForDisplay } from "./datetime-format";
import FilterCell from "./filter-cell";
import {
  googleMapsUrl,
  IParsedLocation,
  isLatInRange,
  isLngInRange,
  parseLocationValue
} from "./location-cell-helpers";
import LockSvg from "./lock.svg";
import { IPendingChanges, IPendingRowInsert, rowPkStringFromRow, RowPkString } from "./pending-changes-reducer";
import { IColumnWidthMap } from "./view-storage";

// Grid sort state. Used in the prop, the onSortChange callback, and the
// cycleSortDir helper below; named so the three signatures don't drift.
export interface IGridSortState {
  column: string;
  dir: "asc" | "desc";
}

// Modifier keys captured at row-gutter click time. shift = range-select
// from the last anchor; meta (cmd / ctrl) = toggle the clicked row in
// or out of the selection.
export interface ISelectionModifiers {
  meta: boolean;
  shift: boolean;
}

// Filter types and operator helpers moved to column-filters.ts so the
// pure logic can be unit-tested without loading React.
export type { IColumnFilterEntry, IColumnFilterMap } from "./column-filters";

export interface IEditableDataGridProps {
  columns: IDatabaseColumnModel[];
  primaryKeyColumns: string[];
  foreignKeys: IManagedDbForeignKey[];
  rows: IManagedDbRow[];
  pendingChanges: IPendingChanges;
  saveIssuesByClientId: Map<string, string>;
  isReadOnly: boolean;
  // True while the initial row fetch (or a refetch) is in flight.
  // The wrapper paints a spinner overlay on top of the grid for the
  // load mid-flight; the grid uses this flag to suppress the in-
  // body empty-state row so it doesn't flash under the spinner
  // before the first response lands.
  isLoading: boolean;
  sort: IGridSortState | null;
  onSortChange: (next: IGridSortState | null) => void;
  // originalValue is the row's persisted value for this column at the
  // moment of the edit. The reducer captures it on the first edit per
  // (pk, column) and preserves it across subsequent edits so the
  // optimistic-concurrency snapshot reflects what the user saw, not
  // their own in-progress value or a refreshed server value.
  onEditCell: (pk: RowPkString, column: string, value: unknown, originalValue: unknown) => void;
  onEditInsertCell: (clientId: string, column: string, value: unknown) => void;
  onAddRow: () => void;
  onRemoveInsert: (clientId: string) => void;
  onDeleteRow: (pk: RowPkString) => void;
  onUndeleteRow: (pk: RowPkString) => void;
  pageStartIndex?: number;
  // Multi-row selection state lives on the parent (table-rows-editor) so the
  // right-click context menu can run exporters over the current selection.
  // DataGrip-style: gutter click selects, cmd/ctrl-click toggles, shift-click
  // selects the contiguous range from the previous click.
  selectedPks: Set<RowPkString>;
  onRowSelectionClick: (pksOnPage: RowPkString[], clickedIndex: number, modifiers: ISelectionModifiers) => void;
  onSelectAllOnPage: (pksOnPage: RowPkString[]) => void;
  // Right-click handler — receives the row's PK so the menu knows what to
  // operate on. If the right-clicked row isn't already selected, the parent
  // promotes it to a single-row selection before showing the menu. The
  // optional `columnName` carries the cell the user actually right-clicked
  // on, which lets the menu add cell-scoped entries (e.g. "Set null")
  // without ambiguity. Falls back to row-only entries when the click landed
  // on the gutter / outside any data cell.
  onRowContextMenu: (clientX: number, clientY: number, pk: RowPkString, columnName?: string) => void;
  // Counterpart for pending insert rows — they have no real PK yet, so we
  // identify them by the client-generated tmp id instead.
  onInsertRowContextMenu: (clientX: number, clientY: number, clientId: string, columnName?: string) => void;
  // Pending insert clientIds that should pulse briefly. Driven by the
  // parent's AI-generate flow; empty in the steady state.
  highlightedInsertIds?: Set<string>;
  // Per-column filters. Keyed by column name; absence (or undefined)
  // means "no filter for this column". For value-bound operators, an
  // empty string also means "no filter" — the consumer hooks drop those
  // entries before sending. `null` from the change callback clears the
  // entry (used when the user empties a non-valueless input or picks
  // a valueless operator after manually clearing).
  columnFilters: IColumnFilterMap;
  onColumnFilterChange: (column: string, next: IColumnFilterEntry | null) => void;
  // Layout overrides — both optional. Absence of a width entry means
  // the column uses the DEFAULT_COLUMN_WIDTH below; presence means the
  // user has manually resized it. Hidden columns are filtered out
  // before any render — they don't reach the colgroup or the cells.
  columnWidths?: IColumnWidthMap;
  hiddenColumns?: readonly string[];
  onColumnWidthChange?: (column: string, width: number) => void;
  // Called when a cell input loses focus. Threads up to the parent
  // hook so it can close the active keystroke-coalescing session in
  // the undo / redo stack — blur is a logical edit boundary even when
  // the user returns to the same cell within the time window.
  onCellEditBlur?: () => void;
}

// Default column width when the user hasn't resized. Tuned for the
// average String column at font-size 13 (the grid's body font); short
// numeric columns will look airy and very-long-text columns will clip
// with ellipsis. Both are intentional — the resize handle is the
// escape hatch for either direction.
const DEFAULT_COLUMN_WIDTH: number = 160;
// Minimum width during a resize drag. Anything below this and the
// header text + sort arrow start overlapping; the handle itself
// becomes hard to grab again.
const MIN_COLUMN_WIDTH: number = 48;

// Operator helpers live in column-filters.ts; re-export so existing
// callers don't have to update their import paths.
export {
  ALL_FILTER_OPERATORS,
  defaultOperatorForColumn,
  isValuelessOperator,
  operatorsForColumn
} from "./column-filters";

// Reuses the existing parameter-type-icons set so column headers match the
// rest of the designer (sql-action editor, table-edit panel, etc.).
const typeIcon = (kuikaType: string | null | undefined): JSX.Element => {
  switch (kuikaType) {
    case "String":
      return <StringSvg width={12} height={12} />;
    case "Int16":
      return <Int16Svg width={12} height={12} />;
    case "Int32":
    case "AutoIncrement":
      return <Int32Svg width={12} height={12} />;
    case "Int64":
      return <Int64Svg width={12} height={12} />;
    case "Decimal":
      return <DecimalSvg width={12} height={12} />;
    case "Double":
      return <DoubleSvg width={12} height={12} />;
    case "Boolean":
      return <BooleanSvg width={12} height={12} />;
    case "Date":
      return <DateSvg width={12} height={12} />;
    case "Datetime":
      return <DateTimeSvg width={12} height={12} />;
    case "DatetimeOffset":
      return <DateTimeOffsetSvg width={12} height={12} />;
    case "Guid":
      return <GuidSvg width={12} height={12} />;
    case "Image":
    case "Signature":
    case "ByteArray":
      return <ImageSvg width={12} height={12} />;
    default:
      return <StringSvg width={12} height={12} />;
  }
};

// Build the structured tooltip body shown when hovering a column
// header. Surfaces the schema metadata the user otherwise has to
// open the table-edit panel for: SQL type (with length / decimal
// scale when present), Kuika data type, nullability, default, FK
// target, unique flag, identity / auto-increment.
//
// Distinct counts and null percentages are deliberately NOT included
// — those need a per-column COUNT(DISTINCT) full scan which can be
// 30s on a big table. Adding them as a hover-instant affordance would
// surprise users with long-running queries; they belong behind an
// explicit "compute stats" affordance, queued for a follow-up.
interface IColumnTooltipProps {
  column: IDatabaseColumnModel;
  outboundFk: IManagedDbForeignKey | undefined;
}

const ColumnHeaderTooltip: React.FC<IColumnTooltipProps> = (props: IColumnTooltipProps): JSX.Element => {
  const { column, outboundFk } = props;
  // Only append a paren'd qualifier when we have a real size to show.
  // String columns carry maxLength → e.g. nvarchar(255). Decimal-shaped
  // columns carry only `decimalScale`; rendering `decimal(2)` looks
  // like the precision when it's actually the scale (decimal(18,2)
  // shows as `decimal(2)`, deeply misleading). The DTO doesn't carry
  // precision, so we omit the qualifier entirely for decimals until
  // the schema layer surfaces both numbers.
  const sqlType: string =
    column.maxLength && column.maxLength > 0 ? `${column.sqlDataType}(${column.maxLength})` : column.sqlDataType;
  const fkTarget: string | null = outboundFk ? `${outboundFk.refTableName}.${outboundFk.refColumnName ?? "Id"}` : null;
  const tags: string[] = [];
  if (column.isPk) {
    tags.push("PK");
  }
  if (column.isIdentity || column.isAutoIncrement) {
    tags.push("identity");
  }
  if (column.isUnique && !column.isPk) {
    tags.push("unique");
  }
  if (column.nullable === false) {
    tags.push("NOT NULL");
  }
  return (
    <div className="kuika-table-editor__col-meta">
      <div className="kuika-table-editor__col-meta-name">{column.name}</div>
      <dl className="kuika-table-editor__col-meta-grid">
        <dt>SQL</dt>
        <dd>
          <code>{sqlType}</code>
        </dd>
        <dt>Kuika</dt>
        <dd>{column.kuikaDataType}</dd>
        {column.defaultValue !== undefined && column.defaultValue !== null && column.defaultValue.length > 0 && (
          <>
            <dt>Default</dt>
            <dd>
              <code>{column.defaultValue}</code>
            </dd>
          </>
        )}
        {fkTarget !== null && (
          <>
            <dt>FK →</dt>
            <dd>
              <code>{fkTarget}</code>
            </dd>
          </>
        )}
      </dl>
      {tags.length > 0 && (
        <div className="kuika-table-editor__col-meta-tags">
          {tags.map((tag: string): JSX.Element => (
            <span key={tag} className="kuika-table-editor__col-meta-tag">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const cycleSortDir = (current: IGridSortState | null, column: string): IGridSortState | null => {
  if (!current || current.column !== column) {
    return { column, dir: "asc" };
  }
  if (current.dir === "asc") {
    return { column, dir: "desc" };
  }
  return null;
};

// Image / Signature read-only cell. The editable variant lives in
// ImageCellEditor with hover popover + lightbox + replace flow; here
// we want the bare minimum that still looks like an image rather
// than a wall of base64. 22 px thumb with the checker plate from
// the editable variant so transparency reads, plus the dims/size
// meta string. On decode failure (corrupt legacy payload, bytes
// that detectImageMime guessed wrong about), the `<img>` would
// otherwise render the browser's native broken-image icon — ugly
// and inconsistent with the editable cell's `⃠` marker. We mirror
// that fallback here via `onError` + local state.
// Compose a download filename from the column name + first PK
// fragment, mirroring the editable cell's convention so files
// downloaded from read-only / system-table rows arrive with names
// distinct from the user's editable downloads.
const buildStaticDownloadFilename = (
  columnName: string | undefined,
  rowKey: string | null | undefined,
  ext: string
): string => {
  const baseName: string =
    typeof columnName === "string" && columnName.length > 0 ? sanitiseFileNamePart(columnName) : "value";
  const rowKeyShort: string = shortRowKeyForFilename(rowKey);
  return rowKeyShort.length > 0 ? `${baseName}-${rowKeyShort}.${ext}` : `${baseName}.${ext}`;
};

interface IStaticImageProps {
  raw: string;
  // Optional inspection-action context. When present, clicking the
  // thumbnail opens a full-size lightbox with a Download button so
  // read-only rows still let the user view + save the image (the
  // browse-only context — no-PK / system / read-only — was
  // previously dropping these inspection affordances entirely).
  columnName?: string;
  rowKey?: string | null;
}
const StaticImage: React.FC<IStaticImageProps> = (props: IStaticImageProps): JSX.Element => {
  const { raw, columnName, rowKey } = props;
  const body: string = stripDataUriPrefix(raw);
  const mime: string = extractDataUriMime(raw) ?? detectImageMime(body);
  const src: string = isDataUri(raw) ? raw : `data:${mime};base64,${body}`;
  const sizeText: string = formatByteSize(approxBase64Bytes(body));
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  // Reset the failure flag when the raw value changes — switching
  // to a different image must get a fresh decode attempt.
  useEffect((): void => {
    setLoadFailed(false);
  }, [src]);

  const handleDownload = (): void => {
    const ext: string = mime === "image/svg+xml" ? "svg" : mime.replace(/^image\//, "");
    const filename: string = buildStaticDownloadFilename(columnName, rowKey, ext.length > 0 ? ext : "bin");
    const outcome: IDownloadOutcome = downloadBase64(body, mime, filename);
    if (!outcome.ok) {
      console.warn("[StaticImage] download failed:", outcome.errorMessage);
    }
  };

  const trigger: JSX.Element = loadFailed ? (
    <span
      className="kuika-table-editor__grid-cell-static-image-broken"
      role="img"
      aria-label="Image bytes are not a recognised format"
      title="Not a valid image"
    >
      ⃠
    </span>
  ) : (
    <img
      src={src}
      alt=""
      className="kuika-table-editor__grid-cell-static-image-img"
      draggable={false}
      onError={(): void => setLoadFailed(true)}
    />
  );

  return (
    <>
      <button
        type="button"
        className="kuika-table-editor__grid-cell-static-image"
        title="Click to preview"
        aria-label="Open image preview"
        disabled={loadFailed}
        onClick={(): void => {
          if (!loadFailed) {
            setLightboxOpen(true);
          }
        }}
      >
        {trigger}
        <span className="kuika-table-editor__grid-cell-static-image-meta">{sizeText}</span>
      </button>
      {lightboxOpen && (
        <KModal
          showModal={lightboxOpen}
          closeModal={(): void => setLightboxOpen(false)}
          header={columnName ?? "Image preview"}
          width={720}
          footer={<KModalFooter submitLabel="Download" onSubmit={handleDownload} />}
        >
          <div className="kuika-table-editor__lightbox">
            <img className="kuika-table-editor__lightbox-image" src={src} alt={columnName ?? ""} />
            <div className="kuika-table-editor__lightbox-meta">{sizeText}</div>
          </div>
        </KModal>
      )}
    </>
  );
};

// Binary read-only chip. Click triggers an immediate download with
// an auto-generated filename (column + short PK fragment + detected
// extension). Read-only is about preventing MUTATION — inspection
// actions like download stay enabled so users on no-PK / system /
// browse-only rows can still get the file out.
interface IStaticBinaryProps {
  raw: string;
  columnName?: string;
  rowKey?: string | null;
}
const StaticBinary: React.FC<IStaticBinaryProps> = (props: IStaticBinaryProps): JSX.Element => {
  const { raw, columnName, rowKey } = props;
  const detected: IDetectedBinary = detectBinaryKind(raw);
  const size: string = formatByteSize(approxBase64Bytes(raw));
  const handleDownload = (): void => {
    const filename: string = buildStaticDownloadFilename(columnName, rowKey, detected.ext);
    const outcome: IDownloadOutcome = downloadBase64(raw, detected.mime, filename);
    if (!outcome.ok) {
      console.warn("[StaticBinary] download failed:", outcome.errorMessage);
    }
  };
  return (
    <button
      type="button"
      className="kuika-table-editor__grid-cell-static-binary"
      title="Click to download"
      aria-label={`Download ${columnName ?? "file"}`}
      onClick={handleDownload}
    >
      {detected.label} · {size}
    </button>
  );
};

// Location read-only — lat, lng with an open-in-maps anchor when
// both coords parse AND fall inside the standard ±90 / ±180 ranges.
// Falls back to the raw stored string when the JSON is malformed.
// The Maps link is gated on the range check (not just non-null) so
// an out-of-range stored value (legacy bad data, schema drift) shows
// the coords without linking to a point that wouldn't render on a
// real map — same contract the editable LocationCellEditor uses for
// its `canOpenInMaps` flag.
interface IStaticLocationProps {
  raw: string;
}
const StaticLocation: React.FC<IStaticLocationProps> = (props: IStaticLocationProps): JSX.Element => {
  const parsed: IParsedLocation = parseLocationValue(props.raw);
  if (parsed.latitude === null || parsed.longitude === null) {
    return <span>{props.raw}</span>;
  }
  const inRange: boolean = isLatInRange(parsed.latitude) && isLngInRange(parsed.longitude);
  return (
    <span className="kuika-table-editor__grid-cell-static-location">
      {parsed.latitude}, {parsed.longitude}
      {inRange && (
        <a
          className="kuika-location-cell__open"
          href={googleMapsUrl(parsed.latitude, parsed.longitude)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open in Google Maps"
          title="Open in Google Maps"
        >
          ↗
        </a>
      )}
    </span>
  );
};

interface IStaticValueContext {
  columnName?: string;
  rowKey?: string | null;
}

const renderStaticValue = (
  value: unknown,
  kuikaDataType?: string | null,
  context?: IStaticValueContext
): JSX.Element => {
  if (value === null || value === undefined) {
    // Lowercase italic ghost — Apple Numbers convention. Reads as
    // "absence" rather than data; the SCSS layer paints it dimmed and
    // italic so the eye scans past it on a busy grid.
    return <span className="kuika-table-editor__grid-cell-null">null</span>;
  }
  if (typeof value === "boolean") {
    return <span>{value ? "true" : "false"}</span>;
  }
  // Complex types — render their display affordance instead of the
  // raw String(value) base64/JSON dump. Read-only stops MUTATION,
  // not inspection: images open a lightbox + Download on click,
  // binary cells trigger Download on click. The editable cell
  // editors still own replace/upload affordances which require
  // mutation.
  if (typeof value === "string" && value.length > 0) {
    if (kuikaDataType === "Image" || kuikaDataType === "Signature") {
      return <StaticImage raw={value} columnName={context?.columnName} rowKey={context?.rowKey} />;
    }
    if (
      kuikaDataType === "FileContent" ||
      kuikaDataType === "ByteArray" ||
      kuikaDataType === "VarBinary" ||
      kuikaDataType === "Binary" ||
      kuikaDataType === "Bytea"
    ) {
      return <StaticBinary raw={value} columnName={context?.columnName} rowKey={context?.rowKey} />;
    }
    if (kuikaDataType === "Location") {
      return <StaticLocation raw={value} />;
    }
  }
  // DateTimeOffset — reformat the ISO offset into a readable "YYYY-MM-DD
  // HH:MM:SS UTC" so the operator never has to mentally parse a `T` and
  // a `+00:00` to know they're looking at UTC.
  if (kuikaDataType === "DatetimeOffset" && typeof value === "string") {
    return <span>{formatDatetimeOffsetForDisplay(value)}</span>;
  }
  return <span>{String(value)}</span>;
};

// Wraps an error-tone cell with a KuikaTooltip so the validation /
// save-issue message becomes hover-AND-focus reachable, replacing the
// browser-native `title` attribute that:
//   * waits ~1.5 s before appearing,
//   * never shows up for keyboard or touch users,
//   * can't carry multi-line content or styling,
//   * truncates long messages depending on the OS.
// The wrapper only mounts a Radix Tooltip for cells that actually
// carry an error — clean / dirty / new cells are returned unchanged so
// we don't pay the Radix Tooltip overhead on the ~10 000 cells a busy
// page can hold.
interface ICellErrorTooltipProps {
  // The cell `<td>` being wrapped — kept as the Tooltip.Trigger via
  // asChild so the trigger's DOM identity is the cell itself.
  children: JSX.Element;
  // Null / empty → no tooltip shown. Critical: the wrapper structure
  // is identical regardless of error state; only the Portal+Content
  // mounts conditionally. Without this, flipping from "no error" to
  // "error" would change the parent component type from a Fragment to
  // a Tooltip wrapper, React would unmount the inner input, and the
  // user's cursor + typing focus would disappear mid-edit.
  errorMessage: string | null;
}

const CellErrorTooltip: React.FC<ICellErrorTooltipProps> = (props: ICellErrorTooltipProps): JSX.Element => {
  const { children, errorMessage } = props;
  const hasError: boolean = errorMessage !== null && errorMessage.length > 0;
  return (
    // Root + Trigger render ALWAYS — the JSX tree shape is
    // identical for "valid" and "invalid" cells. Only the Portal+Content
    // mounts conditionally, and the Content is a sibling of the Trigger
    // in React's reconciliation, not a parent, so its mount / unmount
    // doesn't disturb the children.
    //
    // Snappier than the default 300 ms — error cells benefit from
    // immediate feedback the moment the user reads them, and the red
    // surface is already the cue that something's wrong. The delay sits
    // on the Root: a Provider of its own per cell (thousands on a busy
    // page) also cut these tooltips off from the app-wide provider's
    // warm-up, so moving from one tooltip to the next waited out the
    // delay again instead of opening at once.
    <Tooltip.Root delayDuration={120}>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      {hasError && (
        <Tooltip.Portal>
          <Tooltip.Content className="kuika-tooltip__content kuika-tooltip__content--red" sideOffset={1} side="top">
            <KuikaSmallText>{errorMessage}</KuikaSmallText>
          </Tooltip.Content>
        </Tooltip.Portal>
      )}
    </Tooltip.Root>
  );
};

const EditableDataGrid: React.FC<IEditableDataGridProps> = (props: IEditableDataGridProps): JSX.Element => {
  const {
    columns,
    primaryKeyColumns,
    foreignKeys,
    rows,
    pendingChanges,
    saveIssuesByClientId,
    isReadOnly,
    isLoading,
    sort,
    onSortChange,
    onEditCell,
    onEditInsertCell,
    onAddRow,
    onRemoveInsert,
    onDeleteRow,
    onUndeleteRow,
    pageStartIndex = 0,
    selectedPks,
    onRowSelectionClick,
    onSelectAllOnPage,
    onRowContextMenu,
    onInsertRowContextMenu,
    columnFilters,
    onColumnFilterChange,
    highlightedInsertIds,
    columnWidths,
    hiddenColumns,
    onColumnWidthChange,
    onCellEditBlur
  } = props;

  const noPrimaryKey: boolean = primaryKeyColumns.length === 0;
  const canEdit: boolean = !isReadOnly && !noPrimaryKey;

  // ── Keyboard navigation ─────────────────────────────────────────
  // Roving-tabindex spatial nav over the body cells, matching the data-
  // grid convention from Numbers / Sheets / DataGrip / Supabase. Only
  // one cell carries `tabIndex={0}` at a time, so Tab into the grid
  // lands on the active cell rather than cycling through every input.
  // Arrow keys move the active cell while focus is on a `<td>`; once
  // the user presses Enter, focus moves to the inner input and arrows
  // resume their text-editing meaning. Esc inside an input returns to
  // the cell. State lives at the grid level (only the grid knows its
  // own row × column space) and is bounded by the visible row list
  // (persisted first, then pending inserts).
  interface IActiveCell {
    rowIdx: number;
    colIdx: number;
  }
  const [activeCell, setActiveCell] = useState<IActiveCell | null>(null);
  const tbodyRef = useRef<HTMLTableSectionElement | null>(null);
  // Sourced from props/refs that exist earlier in scope, so the clamp
  // effect can run before `orderedColumns` is computed below — the
  // column count is read lazily from a ref by the effect when it fires.
  const navRowCount: number = rows.length + pendingChanges.inserts.length;
  const navColCountRef = useRef<number>(0);

  // Render every page row directly. We considered windowed
  // virtualization (kept rows-in-viewport only, padded missing height
  // with spacer <tr>s) but the page size is capped at 200 by the
  // query hook, and 200 rows × ~10-15 columns is well within React's
  // rendering budget on hardware shipped this decade. The scroll
  // listener + spacer arithmetic added a real per-render cost AND
  // introduced edge-case "row pops in" flicker at scroll boundaries —
  // not the bar we hold the table editor to. Removed.
  const wrapperRef = useRef<HTMLElement | null>(null);

  // Locate the scroll container (the editor's `__grid-wrapper`) once
  // the tbody has mounted. Used by the active-cell scroll-into-view
  // effect below to anchor scroll behaviour to the right element.
  useEffect((): void => {
    const tbody: HTMLTableSectionElement | null = tbodyRef.current;
    if (tbody === null) {
      return;
    }
    wrapperRef.current = tbody.closest(".kuika-table-editor__grid-wrapper") as HTMLElement | null;
  }, []);

  // Clamp activeCell back into bounds whenever the row/column space
  // shrinks (e.g. a filter change removes the row that was active, or
  // a column gets hidden). Without this the focus effect would query
  // for a cell that no longer exists and silently drop focus.
  useEffect((): void => {
    if (activeCell === null) {
      return;
    }
    const cols: number = navColCountRef.current;
    if (navRowCount === 0 || cols === 0) {
      setActiveCell(null);
      return;
    }
    const nextRow: number = Math.min(activeCell.rowIdx, navRowCount - 1);
    const nextCol: number = Math.min(activeCell.colIdx, cols - 1);
    if (nextRow !== activeCell.rowIdx || nextCol !== activeCell.colIdx) {
      setActiveCell({ rowIdx: nextRow, colIdx: nextCol });
    }
  }, [activeCell, navRowCount]);

  // Focus the active cell whenever it changes. Querying by data attrs
  // beats threading a refs Map through both row maps — the body is
  // small enough that a single querySelector is well under a frame.
  useEffect((): void => {
    if (activeCell === null) {
      return;
    }
    const tbody: HTMLTableSectionElement | null = tbodyRef.current;
    if (tbody === null) {
      return;
    }
    const cell: HTMLElement | null = tbody.querySelector(
      `[data-cell-row="${activeCell.rowIdx}"][data-cell-col="${activeCell.colIdx}"]`
    );
    if (cell === null) {
      // Cell isn't rendered — every cell should always be in the DOM
      // since we render the full page; this branch is defensive
      // against future template changes that would skip rendering.
      return;
    }
    if (document.activeElement !== cell) {
      // Skip focus on insert-cells when the input is already focused
      // (the user just typed into a draft row — don't yank focus back
      // out from under them).
      const inputAlreadyFocused: boolean = document.activeElement !== null && cell.contains(document.activeElement);
      if (!inputAlreadyFocused) {
        cell.focus();
      }
    }
  }, [activeCell, rows.length]);

  const isEditableEventTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) {
      return false;
    }
    const tag: string = target.tagName;
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
  };

  const moveActiveCell = (dRow: number, dCol: number): void => {
    const cols: number = navColCountRef.current;
    if (navRowCount === 0 || cols === 0) {
      return;
    }
    setActiveCell((prev: IActiveCell | null): IActiveCell => {
      const start: IActiveCell = prev ?? { rowIdx: 0, colIdx: 0 };
      return {
        rowIdx: Math.max(0, Math.min(navRowCount - 1, start.rowIdx + dRow)),
        colIdx: Math.max(0, Math.min(cols - 1, start.colIdx + dCol))
      };
    });
  };

  const handleTbodyKeyDown = (e: React.KeyboardEvent<HTMLTableSectionElement>): void => {
    // Esc inside an input → blur the input and stop propagation so
    // ancestor handlers (modal closers etc.) don't fire on top of
    // the input losing focus.
    //
    // Esc on a focused cell (no input) → swallow it. The comment that
    // used to live here noted this was already happening; in fact the
    // non-input branch was returning without `stopPropagation`, and a
    // global Esc handler upstream used to interpret that bubble as
    // "discard all pending edits" — bypassing the 3-edit confirm
    // threshold AND clearing undo history. That global handler is now
    // gone, but stopping propagation here keeps the contract honest if
    // a future ancestor introduces another Esc shortcut.
    if (e.key === "Escape") {
      if (isEditableEventTarget(e.target)) {
        (e.target as HTMLElement).blur();
      }
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    if (isEditableEventTarget(e.target)) {
      // Inside a cell input — arrow keys mean "move the caret", Enter
      // commits via the input's own handlers, Tab uses native behaviour.
      // Don't hijack any of them.
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      moveActiveCell(-1, 0);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      moveActiveCell(1, 0);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      moveActiveCell(0, -1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      moveActiveCell(0, 1);
    } else if (e.key === "Enter" || e.key === "F2") {
      // Enter / F2 → enter edit mode. Find the first focusable input
      // inside the active cell and shift focus to it. Read-only cells
      // (auto-increment, identity) contain no input and Enter becomes
      // a no-op, which matches Numbers / Sheets.
      const target = e.target as HTMLElement;
      const input: HTMLElement | null = target.querySelector<HTMLElement>("input, textarea, select");
      if (input !== null) {
        e.preventDefault();
        input.focus();
        if (input instanceof HTMLInputElement && input.type === "text") {
          input.select();
        }
      }
    } else if (e.key === "Home") {
      e.preventDefault();
      setActiveCell((prev: IActiveCell | null): IActiveCell => ({
        rowIdx: prev?.rowIdx ?? 0,
        colIdx: 0
      }));
    } else if (e.key === "End") {
      e.preventDefault();
      const lastCol: number = Math.max(0, navColCountRef.current - 1);
      setActiveCell((prev: IActiveCell | null): IActiveCell => ({
        rowIdx: prev?.rowIdx ?? 0,
        colIdx: lastCol
      }));
    }
  };

  // Filter the data-grip ordering by user-hidden columns. We hide
  // post-ordering so a hidden column re-appearing in its original slot
  // is intuitive. Hidden columns are dropped at render time only —
  // they still ship in the query payload (the grid filters AFTER the
  // server returns) so re-showing one doesn't trigger a refetch.
  const hiddenColumnSet: ReadonlySet<string> = useMemo((): Set<string> => {
    return new Set(hiddenColumns ?? []);
  }, [hiddenColumns]);
  const orderedColumns: IDatabaseColumnModel[] = useMemo(
    (): IDatabaseColumnModel[] =>
      orderColumnsLikeDataGrip(columns, primaryKeyColumns).filter(
        (c: IDatabaseColumnModel): boolean => !hiddenColumnSet.has(c.name)
      ),
    [columns, primaryKeyColumns, hiddenColumnSet]
  );
  // Mirror the visible-column count so the keyboard-nav effects (which
  // sit higher up in this file, before `orderedColumns` is in scope)
  // can read the current cap lazily. Assigned during render is fine —
  // the value is only consumed inside event handlers + effects that
  // fire AFTER the current render commits.
  navColCountRef.current = orderedColumns.length;

  // Resize state — only set while a drag is in flight. Local to the
  // grid because no one else needs to know; the COMMITTED width is
  // hoisted via onColumnWidthChange on mouseup so the parent's
  // persistence picks it up. Holding the drag in state lets the
  // colgroup re-render at the live size while dragging instead of
  // snapping at release.
  interface IActiveResize {
    column: string;
    startX: number;
    startWidth: number;
    liveWidth: number;
  }
  // Ref mirrors state. The mousemove / mouseup listeners read from the
  // ref so the effect that binds them only re-runs when the drag starts
  // or ends — NOT on every pixel of movement. Previously the effect
  // depended on `activeResize` and rebound on every setState, which (a)
  // dropped mousemove events that fired between cleanup and re-add (the
  // visible "handle lags behind cursor" symptom) and (b) let onUp close
  // over a stale liveWidth if rebinding happened to skip the last move.
  const activeResizeRef = useRef<IActiveResize | null>(null);
  const [activeResize, setActiveResize] = useState<IActiveResize | null>(null);
  // Click on a <th> is the OS-synthesized event after a mousedown + mouseup
  // on (or bubbling into) the same element. Resize handle's mousedown
  // stops propagation, but the user's mouseup typically lands on the th
  // body (cursor moved into it during the drag), so the th still sees a
  // synthetic click and fires sort. We swallow exactly one click after
  // each resize completion.
  const suppressNextHeaderClickRef = useRef<boolean>(false);
  const widthFor = useCallback(
    (columnName: string): number => {
      if (activeResize && activeResize.column === columnName) {
        return activeResize.liveWidth;
      }
      const persisted: number | undefined = columnWidths ? columnWidths[columnName] : undefined;
      return persisted && persisted > 0 ? persisted : DEFAULT_COLUMN_WIDTH;
    },
    [activeResize, columnWidths]
  );

  // Sum of declared col widths (gutter + visible data cols + action).
  // Drives the slack-detection effect below: when the wrapper is wider
  // than this sum, the table would otherwise render at sum-width and
  // appear "shrunk" inside the wrapper. We render a filler col with
  // width: 100% in that case so the extra space gets absorbed.
  const GUTTER_WIDTH_PX: number = 28;
  const ACTION_WIDTH_PX: number = 56;
  const totalDeclaredWidth: number = useMemo((): number => {
    const dataSum: number = orderedColumns.reduce(
      (acc: number, col: IDatabaseColumnModel): number => acc + widthFor(col.name),
      0
    );
    return GUTTER_WIDTH_PX + dataSum + (canEdit ? ACTION_WIDTH_PX : 0);
  }, [orderedColumns, widthFor, canEdit]);

  // Track the wrapper's clientWidth so we can detect when there's
  // horizontal slack to absorb (wrapper > sum of declared cols).
  // ResizeObserver fires on wrapper size changes (window resize,
  // panel resize, etc.). Locate the wrapper directly from tbody
  // here (rather than relying on the wrapper-locate effect
  // declared elsewhere) because effect ordering between sibling
  // useEffects with the same `[]` deps array isn't guaranteed
  // strict — if our locate effect hadn't committed yet,
  // wrapperRef.current would be null and we'd bail before
  // observing.
  const [wrapperWidth, setWrapperWidth] = useState<number>(0);
  useEffect((): (() => void) | undefined => {
    const tbody: HTMLTableSectionElement | null = tbodyRef.current;
    if (tbody === null) {
      return undefined;
    }
    const wrapper: HTMLElement | null = tbody.closest(".kuika-table-editor__grid-wrapper");
    if (wrapper === null) {
      return undefined;
    }
    // Initial measurement so we don't wait for the first
    // ResizeObserver callback to find out the wrapper's width.
    setWrapperWidth(wrapper.clientWidth);
    const observer: ResizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]): void => {
      const entry: ResizeObserverEntry | undefined = entries[0];
      if (entry === undefined) {
        return;
      }
      // contentRect.width excludes border / padding which matches
      // clientWidth's semantics for our purposes (the scrollable
      // content area).
      setWrapperWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(wrapper);
    return (): void => observer.disconnect();
  }, []);
  // True when the wrapper has more horizontal space than the table's
  // declared columns need. A small tolerance keeps the scaling from
  // flipping on/off at the exact boundary during resize drags.
  const SLACK_TOLERANCE_PX: number = 2;
  const hasSlack: boolean = wrapperWidth > totalDeclaredWidth + SLACK_TOLERANCE_PX;

  // When there's slack, scale the DATA cols proportionally so they
  // fill the wrapper (DataGrip / Supabase / Notion convention). Gutter
  // and action stay at their fixed widths. The data-col scale factor
  // is the leftover space distributed back into the data-col total:
  //
  //   wrapper - gutter - action = data-col target
  //   scale = (wrapper - gutter - action) / dataDeclaredSum
  //
  // Active resize anchors at 1.0 so the user's drag isn't fighting an
  // amplified width; otherwise dragging would behave non-linearly
  // (every 1 px of mouse movement would update the persisted width by
  // 1 / scale px, which feels broken on wide wrappers).
  const dataDeclaredSum: number = useMemo((): number => {
    return orderedColumns.reduce((acc: number, col: IDatabaseColumnModel): number => acc + widthFor(col.name), 0);
  }, [orderedColumns, widthFor]);
  const dataScaleFactor: number = useMemo((): number => {
    if (!hasSlack || dataDeclaredSum === 0 || activeResize !== null) {
      return 1;
    }
    const chrome: number = GUTTER_WIDTH_PX + (canEdit ? ACTION_WIDTH_PX : 0);
    const dataTarget: number = wrapperWidth - chrome;
    if (dataTarget <= dataDeclaredSum) {
      return 1;
    }
    return dataTarget / dataDeclaredSum;
  }, [hasSlack, dataDeclaredSum, wrapperWidth, canEdit, activeResize]);
  const renderedWidthFor = useCallback(
    (name: string): number => {
      const base: number = widthFor(name);
      if (dataScaleFactor === 1) {
        return base;
      }
      return Math.round(base * dataScaleFactor);
    },
    [widthFor, dataScaleFactor]
  );

  // Document-level mousemove / mouseup listeners while a drag is in
  // flight. Bound here (rather than on the handle itself) so the user
  // can drag past the table edge without the handle losing pointer
  // capture. The effect's dep is the COLUMN BEING RESIZED (or null)
  // rather than the whole resize state, so a live drag rebinds at most
  // twice (start, end) instead of per-pixel.
  const dragColumn: string | null = activeResize ? activeResize.column : null;
  useEffect((): (() => void) | undefined => {
    if (dragColumn === null) {
      return undefined;
    }
    const onMove = (e: MouseEvent): void => {
      const current: IActiveResize | null = activeResizeRef.current;
      if (current === null) {
        return;
      }
      const delta: number = e.clientX - current.startX;
      const next: number = Math.max(MIN_COLUMN_WIDTH, current.startWidth + delta);
      if (next === current.liveWidth) {
        return;
      }
      const updated: IActiveResize = { ...current, liveWidth: next };
      activeResizeRef.current = updated;
      setActiveResize(updated);
    };
    const onUp = (): void => {
      const final: IActiveResize | null = activeResizeRef.current;
      activeResizeRef.current = null;
      setActiveResize(null);
      // Block the synthetic click on the surrounding <th> so the sort
      // handler doesn't fire as a side-effect of letting go of the handle.
      suppressNextHeaderClickRef.current = true;
      if (final && onColumnWidthChange) {
        onColumnWidthChange(final.column, final.liveWidth);
      }
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return (): void => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [dragColumn, onColumnWidthChange]);

  const startResize = useCallback((column: string, startX: number, currentWidth: number): void => {
    const initial: IActiveResize = { column, startX, startWidth: currentWidth, liveWidth: currentWidth };
    activeResizeRef.current = initial;
    setActiveResize(initial);
  }, []);

  const handleHeaderSortClick = useCallback(
    (columnName: string): void => {
      if (suppressNextHeaderClickRef.current) {
        suppressNextHeaderClickRef.current = false;
        return;
      }
      onSortChange(cycleSortDir(sort, columnName));
    },
    [onSortChange, sort]
  );

  const rowPks: string[] = useMemo((): string[] => {
    return rows.map((row: IManagedDbRow): string => rowPkStringFromRow(row, primaryKeyColumns));
  }, [rows, primaryKeyColumns]);

  // columnName → FK metadata. Cell-editor reads this to decide whether to
  // render the Browse adornment.
  const fkByColumn: Map<string, IManagedDbForeignKey> = useMemo((): Map<string, IManagedDbForeignKey> => {
    const map = new Map<string, IManagedDbForeignKey>();
    foreignKeys.forEach((fk: IManagedDbForeignKey): void => {
      if (fk?.columnName) {
        map.set(fk.columnName, fk);
      }
    });
    return map;
  }, [foreignKeys]);

  // Capture-phase contextmenu listener at the document level. This beats any
  // parent that might preventDefault + stopPropagation in the bubble phase
  // (the canvas, antd wrappers, react-flow, etc.) and reliably catches the
  // right-click regardless of which child element is the actual target. We
  // scope by the data-table-editor marker on our table so other tables in
  // the app are unaffected.
  useEffect((): (() => void) => {
    const handler = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target) {
        return;
      }
      const table: HTMLElement | null = target.closest("[data-table-editor]");
      if (!table) {
        return;
      }
      // Identify the cell the user actually right-clicked on so the
      // menu can offer cell-scoped entries (Set null, etc.) without
      // ambiguity. Falls back to `undefined` for clicks on the gutter
      // / outside any data cell, which keeps the menu row-only there.
      const cellEl: HTMLElement | null = target.closest("td[data-cell-col]");
      let cellColumnName: string | undefined = undefined;
      if (cellEl !== null) {
        const colAttr: string | null = cellEl.getAttribute("data-cell-col");
        if (colAttr !== null) {
          const colIdx: number = Number(colAttr);
          if (Number.isInteger(colIdx) && colIdx >= 0 && colIdx < orderedColumns.length) {
            cellColumnName = orderedColumns[colIdx]!.name;
          }
        }
      }
      // Persisted row first; pending inserts use a separate marker because
      // they don't have a real PK to key off yet.
      const rowEl: HTMLElement | null = target.closest("tr[data-pk]");
      if (rowEl) {
        const pk: string | null = rowEl.getAttribute("data-pk");
        if (pk !== null) {
          e.preventDefault();
          e.stopPropagation();
          onRowContextMenu(e.clientX, e.clientY, pk, cellColumnName);
        }
        return;
      }
      const insertEl: HTMLElement | null = target.closest("tr[data-client-id]");
      if (insertEl) {
        const clientId: string | null = insertEl.getAttribute("data-client-id");
        if (clientId !== null) {
          e.preventDefault();
          e.stopPropagation();
          onInsertRowContextMenu(e.clientX, e.clientY, clientId, cellColumnName);
        }
      }
    };
    // capture: true wins over any bubble-phase preventDefault.
    document.addEventListener("contextmenu", handler, true);
    return (): void => document.removeEventListener("contextmenu", handler, true);
  }, [onRowContextMenu, onInsertRowContextMenu, orderedColumns]);

  const allOnPageSelected: boolean = useMemo((): boolean => {
    if (rowPks.length === 0) {
      return false;
    }
    return rowPks.every((pk: string): boolean => selectedPks.has(pk));
  }, [rowPks, selectedPks]);

  // Cell expander modal — double-clicking a text/Guid cell opens it. Boolean
  // / date / numeric cells skip (inline editors are already optimal for them).
  const [expander, setExpander] = useState<null | {
    column: IDatabaseColumnModel;
    value: unknown;
    onApply: (next: string | null) => void;
  }>(null);
  // Only String benefits from the larger expander modal — a Guid is a
  // fixed 36 chars and fits any cell. Used to gate the "Expand"
  // adornment on the cell editor.
  const isExpandableType = (col: IDatabaseColumnModel): boolean => {
    const t: string = col.kuikaDataType ?? "String";
    return t === "String";
  };

  // Coerce a pasted string value to the column's natural runtime
  // type. Without this, paste shipped raw strings into every cell
  // regardless of column type — numeric columns would hold "42"
  // (string) instead of 42 (number), which then:
  //   * Cause local validation to flag the row "string in numeric
  //     column".
  //   * Make the type="number" input render the string then re-fire
  //     onChange with the coerced number on next focus — a silent
  //     type flip that confuses both the undo stack and the dirty-
  //     detection logic.
  //   * Risk server-side parse failure on save.
  //
  // Empty string → null matches the cell editors' own "empty = null"
  // convention. Boolean accepts the obvious tokens ("true" / "false"
  // / "1" / "0" / case-insensitive); anything else stays as a string
  // so validation can flag it. High-precision numerics + dates stay
  // as strings — their editors KEEP them as strings to preserve
  // precision / format. Everything else passes through.
  const coercePastedValue = (text: string, kuikaDataType: string | null | undefined): unknown => {
    if (text === "") {
      return null;
    }
    const type: string = kuikaDataType ?? "";
    if (type === "Boolean") {
      const lower: string = text.toLowerCase().trim();
      if (lower === "true" || lower === "1") return true;
      if (lower === "false" || lower === "0") return false;
      return text;
    }
    if (type === "Int16" || type === "Int32" || type === "AutoIncrement" || type === "Double") {
      const num: number = Number(text);
      return Number.isFinite(num) ? num : text;
    }
    // Int64 / Decimal: KEEP as string. The cell editor for these
    // uses an inputMode + pattern + string-passthrough chain
    // precisely because Number() loses precision on values past 2^53.
    // Date / Datetime / DatetimeOffset: editor reads strings.
    // String / Guid / Image / Binary / Location / Object / etc.: pass through.
    return text;
  };

  // ── Excel / TSV paste — Numbers / DataGrip / Supabase convention ──
  // User selects a block of cells in a spreadsheet, copies, lands on
  // a target cell in our grid, hits ⌘V / Ctrl+V. We split the
  // clipboard text on \r?\n (rows) and \t (cols) and dispatch one
  // edit per cell starting at the active cell's position.
  //
  // Single-cell paste (no tab, no newline) falls through to the
  // browser's default paste behavior so a focused text input keeps
  // its native paste semantics. Multi-cell paste is intercepted
  // even when an input is focused — the input would otherwise eat
  // tabs + newlines as literal characters, which is never what the
  // user wants.
  //
  // Cells that fail any of the "can write" gates (no active cell,
  // immutable column, deleted row, out of bounds, read-only grid)
  // are skipped silently. The skipped count surfaces only via the
  // cell visual state (the un-pasted cell stays as it was), which
  // matches the rest of the editor's "errors highlight in place"
  // pattern.
  const handleTbodyPaste = (e: React.ClipboardEvent<HTMLTableSectionElement>): void => {
    if (!canEdit) {
      return;
    }
    if (activeCell === null) {
      return;
    }
    const text: string = e.clipboardData.getData("text/plain");
    if (text.length === 0) {
      return;
    }
    // Strip a trailing newline (Excel copy always appends one) so we
    // don't generate an empty trailing row.
    const trimmed: string = text.replace(/\r?\n$/, "");
    const lines: string[] = trimmed.split(/\r?\n/);
    const hasTab: boolean = trimmed.indexOf("\t") !== -1;
    const isMultiCell: boolean = lines.length > 1 || hasTab;
    if (!isMultiCell) {
      // Single-cell value paste — let the focused input handle it
      // natively. Tab-completion / undo-redo are nicer through the
      // input's own pipeline than through synthesized editCells.
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    const startRow: number = activeCell.rowIdx;
    const startCol: number = activeCell.colIdx;
    const insertsArray: IPendingRowInsert[] = pendingChanges.inserts;
    lines.forEach((rowText: string, rOffset: number): void => {
      const cells: string[] = rowText.split("\t");
      cells.forEach((cellText: string, cOffset: number): void => {
        const targetRowIdx: number = startRow + rOffset;
        const targetColIdx: number = startCol + cOffset;
        // Column bounds
        if (targetColIdx < 0 || targetColIdx >= orderedColumns.length) {
          return;
        }
        const col: IDatabaseColumnModel = orderedColumns[targetColIdx]!;
        // Immutable cell — skip (PK / identity / auto-increment).
        // Server would reject the mutation anyway; skipping keeps
        // the user's source data aligned with the next visible
        // cell instead of silently dropping the whole row.
        if (col.isPk === true || col.isIdentity === true || col.isAutoIncrement === true) {
          return;
        }
        // Route through the same type coercion the cell editors do
        // on direct typing, so paste produces a "42" → 42 number
        // for numeric columns instead of a raw string the reducer
        // / validator / server would see as a type mismatch.
        const coerced: unknown = coercePastedValue(cellText, col.kuikaDataType);
        // Persisted row region (0 .. rows.length - 1)?
        if (targetRowIdx >= 0 && targetRowIdx < rows.length) {
          const pk: string = rowPks[targetRowIdx];
          if (pendingChanges.deletes.has(pk)) {
            return;
          }
          const row: IManagedDbRow = rows[targetRowIdx]!;
          const originalValue: unknown = row[col.name];
          onEditCell(pk, col.name, coerced, originalValue);
          return;
        }
        // Pending-insert region (rows.length .. rows.length + inserts.length - 1)?
        const insertOffset: number = targetRowIdx - rows.length;
        if (insertOffset >= 0 && insertOffset < insertsArray.length) {
          const insert: IPendingRowInsert = insertsArray[insertOffset]!;
          onEditInsertCell(insert.clientId, col.name, coerced);
          return;
        }
        // Out of bounds row — silently clip. Future enhancement
        // could auto-add insert rows for overflow, but that's a
        // separate UX decision (would change the "+N pending"
        // counter unexpectedly).
      });
    });
  };

  return (
    <>
      <table className="kuika-table-editor__grid" data-testid="tableEditorGrid" data-table-editor="true">
        {/* <colgroup> + table-layout: fixed in the SCSS gives every column
            a stable, user-controllable width. Without this, the browser
            would auto-size to the widest cell on the page and re-flow
            on every page change. */}
        <colgroup>
          <col className="kuika-table-editor__col-gutter" />
          {orderedColumns.map((col: IDatabaseColumnModel): JSX.Element => (
            <col key={col.id ?? col.name} style={{ width: `${renderedWidthFor(col.name)}px` }} />
          ))}
          {canEdit && <col className="kuika-table-editor__col-action" />}
        </colgroup>
        <thead>
          <tr>
            <th
              className={
                "kuika-table-editor__gutter kuika-table-editor__gutter--clickable" +
                (allOnPageSelected ? " is-all-selected" : "")
              }
              data-sortable="false"
              title={allOnPageSelected ? "Clear page selection" : "Select all on page"}
              onClick={(): void => onSelectAllOnPage(rowPks)}
            >
              #
            </th>
            {orderedColumns.map((col: IDatabaseColumnModel): JSX.Element => {
              const isSorted: boolean = sort?.column === col.name;
              const arrow: string | null = isSorted ? (sort?.dir === "asc" ? "↑" : "↓") : null;
              const outboundFk: IManagedDbForeignKey | undefined = foreignKeys.find(
                (fk: IManagedDbForeignKey): boolean => fk.columnName === col.name
              );
              const currentWidth: number = widthFor(col.name);
              return (
                <th
                  key={col.id ?? col.name}
                  className="is-sortable"
                  onClick={(): void => handleHeaderSortClick(col.name)}
                >
                  <KuikaTooltip
                    content={<ColumnHeaderTooltip column={col} outboundFk={outboundFk} />}
                    position="bottom"
                  >
                    <span className="kuika-table-editor__grid-th-inner">
                      {typeIcon(col.kuikaDataType)}
                      {col.isPk && <span className="kuika-table-editor__pk-mark">PK</span>}
                      <span>{col.name}</span>
                      {arrow && <span className="kuika-table-editor__sort-arrow">{arrow}</span>}
                    </span>
                  </KuikaTooltip>
                  {/* Resize grab handle on the right edge. Capture-phase
                      stopPropagation on mousedown keeps the click from
                      tripping the sort handler on the surrounding <th>. */}
                  {onColumnWidthChange && (
                    <span
                      className="kuika-table-editor__col-resize-handle"
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize ${col.name}`}
                      onMouseDown={(e: React.MouseEvent): void => {
                        e.stopPropagation();
                        e.preventDefault();
                        startResize(col.name, e.clientX, currentWidth);
                      }}
                      onClick={(e: React.MouseEvent): void => {
                        // Sort would otherwise fire when the user clicks
                        // the handle without dragging — stop here.
                        e.stopPropagation();
                      }}
                    />
                  )}
                </th>
              );
            })}
            {canEdit && <th className="kuika-table-editor__grid-action-cell" data-sortable="false" />}
          </tr>
          <tr className="kuika-table-editor__filter-row">
            <th className="kuika-table-editor__gutter kuika-table-editor__filter-row-gutter" data-sortable="false" />
            {orderedColumns.map((col: IDatabaseColumnModel): JSX.Element => {
              const entry: IColumnFilterEntry | undefined = columnFilters[col.name];
              return (
                <th key={col.id ?? col.name} className="kuika-table-editor__filter-row-cell">
                  <FilterCell
                    column={col}
                    entry={entry}
                    onChange={(next: IColumnFilterEntry | null): void => onColumnFilterChange(col.name, next)}
                  />
                </th>
              );
            })}
            {canEdit && <th className="kuika-table-editor__filter-row-cell" data-sortable="false" />}
          </tr>
        </thead>
        <tbody ref={tbodyRef} onKeyDown={handleTbodyKeyDown} onPaste={handleTbodyPaste}>
          {/* Empty-state row — distinguish "no rows at all" from
              "filter too tight" so the user knows which lever to
              pull. Skipped when there are pending inserts, because
              the user is actively adding rows and the insert
              section below is the load-bearing UI for that path.
              Skipped during load — the wrapper spinner covers that
              state, otherwise the "table is empty" copy flashes
              under the spinner before the first response lands. */}
          {!isLoading && rows.length === 0 && pendingChanges.inserts.length === 0 && (
            <tr className="kuika-table-editor__grid-empty-row">
              <td colSpan={orderedColumns.length + (canEdit ? 2 : 1)}>
                {buildColumnFilters(columnFilters).length > 0 ? (
                  <div className="kuika-table-editor__grid-empty">
                    <div className="kuika-table-editor__grid-empty-title">No rows match the current filter.</div>
                    <div className="kuika-table-editor__grid-empty-desc">
                      Adjust an operator or value above, or clear all filters from the toolbar.
                    </div>
                  </div>
                ) : (
                  <div className="kuika-table-editor__grid-empty">
                    <div className="kuika-table-editor__grid-empty-title">This table is empty.</div>
                    <div className="kuika-table-editor__grid-empty-desc">
                      {canEdit ? "Click + Insert row below to add the first one." : "No rows have been written yet."}
                    </div>
                  </div>
                )}
              </td>
            </tr>
          )}
          {rows.map((row: IManagedDbRow, rowIdx: number): JSX.Element => {
            const pk: string = rowPks[rowIdx];
            const isDeleted: boolean = pendingChanges.deletes.has(pk);
            const rowIssue: string | undefined = saveIssuesByClientId.get(pk);
            const pendingUpdate = pendingChanges.updates.get(pk);
            const isSelected: boolean = selectedPks.has(pk);
            const rowClass: string =
              (isDeleted ? "kuika-table-editor__grid-row--deleted" : "") +
              (rowIssue !== undefined ? " kuika-table-editor__grid-row--has-error" : "") +
              (isSelected ? " kuika-table-editor__grid-row--selected" : "");
            // First PK column's value as a string — used by binary
            // and image cell editors to disambiguate download
            // filenames across rows. Falls through to null on tables
            // without a PK; cells then default to plain
            // `<column>.<ext>` filenames.
            const rowKey: string | null =
              primaryKeyColumns.length > 0 && row[primaryKeyColumns[0]!] !== undefined
                ? String(row[primaryKeyColumns[0]!])
                : null;
            return (
              <tr key={pk} className={rowClass} data-pk={pk} title={rowIssue}>
                <td
                  className="kuika-table-editor__gutter kuika-table-editor__gutter--clickable"
                  title="Select row (Shift to extend, Ctrl/Cmd to toggle)"
                  onClick={(e: React.MouseEvent): void => {
                    // Cmd on macOS, Ctrl elsewhere — both treated as "toggle".
                    onRowSelectionClick(rowPks, rowIdx, { meta: e.metaKey || e.ctrlKey, shift: e.shiftKey });
                  }}
                >
                  {pageStartIndex + rowIdx + 1}
                </td>
                {orderedColumns.map((col: IDatabaseColumnModel, colIdx: number): JSX.Element => {
                  const original: unknown = row[col.name];
                  const hasPendingValue: boolean =
                    pendingUpdate !== undefined &&
                    Object.prototype.hasOwnProperty.call(pendingUpdate.changes, col.name);
                  const value: unknown = hasPendingValue ? pendingUpdate!.changes[col.name] : original;
                  const dirty: boolean = hasPendingValue && pendingUpdate!.changes[col.name] !== original;
                  const visualState: ITableEditorCellVisualState = persistedCellVisualState({
                    column: col,
                    value,
                    dirty,
                    saveIssue: rowIssue
                  });
                  const isImmutableColumn: boolean =
                    col.isPk === true || col.isIdentity === true || col.isAutoIncrement === true;
                  const cellIsActive: boolean =
                    activeCell !== null && activeCell.rowIdx === rowIdx && activeCell.colIdx === colIdx;
                  if (!canEdit || isDeleted || isImmutableColumn) {
                    return (
                      <td
                        key={col.name}
                        className="kuika-table-editor__grid-cell"
                        data-testid={`tableEditorCell-${col.name}-${rowIdx}`}
                        data-cell-row={rowIdx}
                        data-cell-col={colIdx}
                        tabIndex={cellIsActive ? 0 : -1}
                        onFocus={(): void => {
                          if (!cellIsActive) setActiveCell({ rowIdx, colIdx });
                        }}
                      >
                        <span className="kuika-table-editor__grid-cell-static kuika-table-editor__grid-cell-locked">
                          {isImmutableColumn && canEdit && !isDeleted && (
                            <span className="kuika-table-editor__grid-cell-lock" aria-label="Immutable">
                              <LockSvg />
                            </span>
                          )}
                          <span className="kuika-table-editor__grid-cell-value">
                            {renderStaticValue(value, col.kuikaDataType, { columnName: col.name, rowKey })}
                          </span>
                        </span>
                      </td>
                    );
                  }
                  const cellNode: JSX.Element = (
                    <td
                      key={col.name}
                      className="kuika-table-editor__grid-cell"
                      data-testid={`tableEditorCell-${col.name}-${rowIdx}`}
                      data-dirty={dirty ? "true" : "false"}
                      data-cell-state={visualState.tone}
                      data-cell-row={rowIdx}
                      data-cell-col={colIdx}
                      tabIndex={cellIsActive ? 0 : -1}
                      onFocus={(): void => {
                        if (!cellIsActive) setActiveCell({ rowIdx, colIdx });
                      }}
                    >
                      <CellEditor
                        column={col}
                        value={value}
                        rowKey={rowKey}
                        foreignKey={fkByColumn.get(col.name)}
                        onChange={(next: unknown): void => onEditCell(pk, col.name, next, original)}
                        onBlur={onCellEditBlur}
                        onRequestExpand={
                          isExpandableType(col)
                            ? (): void =>
                                setExpander({
                                  column: col,
                                  value,
                                  onApply: (next: string | null): void => onEditCell(pk, col.name, next, original)
                                })
                            : undefined
                        }
                      />
                    </td>
                  );
                  const cellErrorMessage: string | null =
                    visualState.tone === "error" && visualState.title ? visualState.title : null;
                  return (
                    <CellErrorTooltip key={col.name} errorMessage={cellErrorMessage}>
                      {cellNode}
                    </CellErrorTooltip>
                  );
                })}
                {canEdit && (
                  <td className="kuika-table-editor__grid-action-cell">
                    {isDeleted ? (
                      <button
                        type="button"
                        className="kuika-table-editor__row-action undo"
                        onClick={(): void => onUndeleteRow(pk)}
                        title="Undo delete"
                      >
                        Undo
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="kuika-table-editor__row-action"
                        onClick={(): void => onDeleteRow(pk)}
                        title="Mark row for deletion"
                      >
                        ×
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
          {canEdit &&
            pendingChanges.inserts.map((insert: IPendingRowInsert, insertIdx: number): JSX.Element => {
              const isHighlighted: boolean = highlightedInsertIds?.has(insert.clientId) ?? false;
              const rowIssue: string | undefined = saveIssuesByClientId.get(insert.clientId);
              const insertHasAnyValue: boolean = Object.keys(insert.values).length > 0;
              const insertNavRow: number = rows.length + insertIdx;
              const rowClass: string =
                "kuika-table-editor__grid-row--inserted" +
                (rowIssue !== undefined ? " kuika-table-editor__grid-row--has-error" : "") +
                (isHighlighted ? " kuika-table-editor__grid-row--highlight" : "");
              return (
                <tr
                  key={insert.clientId}
                  className={rowClass}
                  data-testid="tableEditorInsertRow"
                  data-client-id={insert.clientId}
                  title={rowIssue}
                >
                  <td className="kuika-table-editor__gutter">+{insertIdx + 1}</td>
                  {orderedColumns.map((col: IDatabaseColumnModel, colIdx: number): JSX.Element => {
                    const hasEditedValue: boolean = Object.prototype.hasOwnProperty.call(insert.values, col.name);
                    const value: unknown = insert.values[col.name];
                    const visualState: ITableEditorCellVisualState = insertCellVisualState({
                      column: col,
                      value,
                      insertHasAnyValue,
                      hasEditedValue,
                      saveIssue: rowIssue
                    });
                    const cellIsActive: boolean =
                      activeCell !== null && activeCell.rowIdx === insertNavRow && activeCell.colIdx === colIdx;
                    if (col.isIdentity || col.isAutoIncrement) {
                      return (
                        <td
                          key={col.name}
                          className="kuika-table-editor__grid-cell"
                          data-testid={`tableEditorInsertCell-${col.name}-${insertIdx}`}
                          data-cell-row={insertNavRow}
                          data-cell-col={colIdx}
                          tabIndex={cellIsActive ? 0 : -1}
                          onFocus={(): void => {
                            if (!cellIsActive) setActiveCell({ rowIdx: insertNavRow, colIdx });
                          }}
                        >
                          <span className="kuika-table-editor__grid-cell-static kuika-table-editor__grid-cell-null">
                            auto
                          </span>
                        </td>
                      );
                    }
                    const cellNode: JSX.Element = (
                      <td
                        key={col.name}
                        className="kuika-table-editor__grid-cell"
                        data-testid={`tableEditorInsertCell-${col.name}-${insertIdx}`}
                        data-cell-state={visualState.tone}
                        data-cell-row={insertNavRow}
                        data-cell-col={colIdx}
                        tabIndex={cellIsActive ? 0 : -1}
                        onFocus={(): void => {
                          if (!cellIsActive) setActiveCell({ rowIdx: insertNavRow, colIdx });
                        }}
                      >
                        <CellEditor
                          column={col}
                          value={value}
                          foreignKey={fkByColumn.get(col.name)}
                          onChange={(next: unknown): void => onEditInsertCell(insert.clientId, col.name, next)}
                          onBlur={onCellEditBlur}
                          onRequestExpand={
                            isExpandableType(col)
                              ? (): void =>
                                  setExpander({
                                    column: col,
                                    value,
                                    onApply: (next: string | null): void =>
                                      onEditInsertCell(insert.clientId, col.name, next)
                                  })
                              : undefined
                          }
                        />
                      </td>
                    );
                    const cellErrorMessage: string | null =
                      visualState.tone === "error" && visualState.title ? visualState.title : null;
                    return (
                      <CellErrorTooltip key={col.name} errorMessage={cellErrorMessage}>
                        {cellNode}
                      </CellErrorTooltip>
                    );
                  })}
                  <td className="kuika-table-editor__grid-action-cell">
                    <button
                      type="button"
                      className="kuika-table-editor__row-action"
                      onClick={(): void => onRemoveInsert(insert.clientId)}
                      title="Discard this new row"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          {canEdit && (
            <tr className="kuika-table-editor__grid-add-row">
              <td colSpan={columns.length + 2}>
                <button
                  type="button"
                  className="kuika-table-editor__add-row-btn"
                  data-testid="tableEditorAddRowButton"
                  onClick={onAddRow}
                >
                  <span className="plus">+</span>
                  Insert row
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {expander && (
        <CellExpanderModal
          isOpen
          column={expander.column}
          initialValue={expander.value}
          onApply={expander.onApply}
          onClose={(): void => setExpander(null)}
        />
      )}
    </>
  );
};

export default EditableDataGrid;
